import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdAdd, MdSearch, MdClose, MdEdit, MdDelete } from 'react-icons/md';
import { useToast } from '../../components/shared/Toast';
import { authenticatedFetch } from '../../lib/api';
import { CurrencyAmountInput } from '../../components/shared/CurrencyAmountInput';
import { loadStoredCurrency } from '../../lib/currency';
import {
  ClearFiltersButton,
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
  TableBodySkeleton,
} from '@euroasiann/shared/ui';

interface Customer {
  _id: string;
  organizationId?: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  primaryContact: string;
  numberOfVessels: number;
  isActive: boolean;
  status: string;
  createdAt?: string;
}

function mapOnboardingRow(raw: Record<string, unknown>): Customer {
  const orgId = raw.organizationId;
  return {
    _id: String(raw._id ?? ''),
    organizationId: orgId ? String(orgId) : undefined,
    companyName: String(raw.companyName ?? ''),
    email: String(raw.email ?? ''),
    phoneNumber: String(raw.mobilePhone ?? raw.deskPhone ?? raw.phoneNumber ?? ''),
    primaryContact: String(raw.contactPerson ?? raw.primaryContact ?? ''),
    numberOfVessels: Number(raw.vessels ?? raw.numberOfVessels ?? 0),
    isActive: Boolean(raw.isActive),
    status: String(raw.status ?? ''),
    createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
  };
}

export function CustomersPage() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('accepted');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    primaryContact: '',
    numberOfVessels: '',
    chargePerVessel: '',
    chargePerVesselCurrency: loadStoredCurrency(),
  });

  const inviteCustomerMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        organizationName: formData.companyName.trim(),
        organizationType: 'customer',
        adminEmail: formData.email.trim().toLowerCase(),
        firstName: formData.primaryContact.trim().split(' ')[0] || 'Customer',
        lastName: formData.primaryContact.trim().split(' ').slice(1).join(' ') || 'Admin',
        phone: formData.phone.trim() || undefined,
        numberOfVessels: formData.numberOfVessels ? Number(formData.numberOfVessels) : undefined,
        chargePerVessel: Number(formData.chargePerVessel || 0),
        chargePerVesselCurrency: formData.chargePerVesselCurrency,
        isActive: true,
      };

      const response = await authenticatedFetch('/api/v1/admin/organizations/invite', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || data?.message || 'Failed to invite customer');
      }
      return data;
    },
    onSuccess: () => {
      showToast('Customer invited successfully. Invitation email has been triggered.', 'success');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      handleCloseModal();
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to invite customer', 'error');
    },
  });

  // Fetch customers
  const { data: customers = [], isLoading } = useQuery<Customer[]>({
    queryKey: ['customers', activeTab],
    queryFn: async () => {
      // Backend exposes customer-onboardings endpoint, not /admin/customers.
      // "Accepted" in UI maps to approved onboardings; "Pending" maps to completed submissions awaiting approval.
      const status = activeTab === 'accepted' ? 'approved' : 'completed';
      const response = await authenticatedFetch(`/api/v1/admin/customer-onboardings?status=${status}`);
      if (!response.ok) throw new Error('Failed to fetch customers');
      const data = await response.json();
      const rows = Array.isArray(data.data) ? data.data : [];
      return rows.map((row: Record<string, unknown>) => mapOnboardingRow(row));
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async (customer: Customer) => {
      if (!customer.organizationId) {
        throw new Error('This customer has no linked organization yet and cannot be deleted from here.');
      }

      const response = await authenticatedFetch(
        `/api/v1/admin/organizations/${customer.organizationId}`,
        { method: 'DELETE' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || data?.message || 'Failed to delete organization');
      }
      return data;
    },
    onSuccess: () => {
      showToast('Customer organization deleted successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete customer', 'error');
    },
  });

  const handleDeleteCustomer = (customer: Customer) => {
    if (
      !window.confirm(
        `Delete ${customer.companyName}? This removes the organization, its users, and related access. This cannot be undone.`
      )
    ) {
      return;
    }
    deleteCustomerMutation.mutate(customer);
  };

  // Filter customers by search query
  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers;
    const query = searchQuery.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.companyName.toLowerCase().includes(query) ||
        customer.email.toLowerCase().includes(query) ||
        customer.phoneNumber.toLowerCase().includes(query) ||
        customer.primaryContact.toLowerCase().includes(query)
    );
  }, [customers, searchQuery]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      companyName: '',
      email: '',
      phone: '',
      primaryContact: '',
      numberOfVessels: '',
      chargePerVessel: '',
      chargePerVesselCurrency: loadStoredCurrency(),
    });
  };

  const handleSubmit = () => {
    if (!formData.companyName.trim() || !formData.email.trim() || !formData.primaryContact.trim()) {
      showToast('Company name, email, and primary contact are required', 'error');
      return;
    }
    if (!formData.chargePerVessel.trim() || Number(formData.chargePerVessel) <= 0) {
      showToast('Per vessel price is required', 'error');
      return;
    }
    inviteCustomerMutation.mutate();
  };

  return (
    <div className="w-full min-h-screen p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
            <span className={erpPageBreadcrumbItemClass}>Admin</span>
            <span className={erpPageBreadcrumbSepClass}>/</span>
            <span className={erpPageBreadcrumbCurrentClass}>Dashboard</span>
          </nav>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">All Customers</h1>
        </div>
        <button
          onClick={handleOpenModal}
          className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors"
        >
          <MdAdd className="w-5 h-5" />
          Add Customer
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-[hsl(var(--border))]">
        <button
          onClick={() => setActiveTab('accepted')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'accepted'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Accepted
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'pending'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Pending
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0088cc]/65 w-5 h-5" />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
          />
        </div>
        <ClearFiltersButton
          visible={!!searchQuery.trim()}
          onClick={() => setSearchQuery('')}
          variant="toolbar"
        />
      </div>

      {/* Table */}
      <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[hsl(var(--secondary))]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">S. No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Company Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Phone Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Primary Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">no. of Vessels</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-[hsl(var(--card))] divide-y divide-[#0088cc]/20 dark:divide-gray-800">
              {isLoading ? (
                <TableBodySkeleton rows={6} cols={7} />
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-[hsl(var(--muted-foreground))]">
                    {activeTab === 'accepted'
                      ? "No accepted customers found. A list of all accepted Customers."
                      : "No pending customers found. A list of all pending Customers."}
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer, index) => (
                  <tr key={customer._id} className="hover:bg-[hsl(var(--secondary))]/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--foreground))]">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[hsl(var(--foreground))]">
                      {customer.companyName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--foreground))]">
                      {customer.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--foreground))]">
                      {customer.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--foreground))]">
                      {customer.primaryContact}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--foreground))]">
                      {customer.numberOfVessels}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[hsl(var(--foreground))]">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-[#0088cc] hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit"
                        >
                          <MdEdit className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-40"
                          title={customer.organizationId ? 'Delete organization' : 'No organization linked yet'}
                          disabled={!customer.organizationId || deleteCustomerMutation.isPending}
                          onClick={() => handleDeleteCustomer(customer)}
                        >
                          <MdDelete className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Customer Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-[hsl(var(--card))] rounded-lg shadow-xl w-full max-w-md mx-4 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))]">
              <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Add New Customer</h2>
              <button
                onClick={handleCloseModal}
                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] dark:hover:text-gray-200"
              >
                <MdClose className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                Enter the details for the new customer.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Primary Contact
                  </label>
                  <input
                    type="text"
                    value={formData.primaryContact}
                    onChange={(e) => setFormData({ ...formData, primaryContact: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter primary contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Number of Vessels
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.numberOfVessels}
                    onChange={(e) => setFormData({ ...formData, numberOfVessels: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter number of vessels"
                  />
                </div>
                <CurrencyAmountInput
                  label="Per Vessel Price"
                  value={formData.chargePerVessel}
                  onChange={(v) => setFormData({ ...formData, chargePerVessel: v })}
                  currencyCode={formData.chargePerVesselCurrency}
                  onCurrencyCodeChange={(code) =>
                    setFormData({ ...formData, chargePerVesselCurrency: code })
                  }
                  required
                  helperText="This amount will be charged for every vessel the customer adds."
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[hsl(var(--border))]">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 border border-[#0088cc] text-[hsl(var(--primary))] rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={inviteCustomerMutation.isPending}
                className="px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {inviteCustomerMutation.isPending ? 'Sending...' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


